import { splitVersionChangeLabel } from "@nexus/contracts";

import type { PersonaScriptDto } from "@/features/persona-opportunities/api/persona-opportunity-scripts.api";

export type PersonaScriptHighlightField = "seeing" | "hearing" | "product" | "props";

export function personaScriptSceneFieldDiffersFromOriginal(
  preview: PersonaScriptDto | undefined,
  original: PersonaScriptDto | undefined,
  sceneNumber: number,
  field: PersonaScriptHighlightField,
): boolean {
  if (!preview || !original) return false;
  const previewScene = preview.scenes.find((scene) => scene.sceneNumber === sceneNumber);
  const originalScene = original.scenes.find((scene) => scene.sceneNumber === sceneNumber);
  if (!previewScene || !originalScene) return Boolean(previewScene) !== Boolean(originalScene);
  return (previewScene[field] ?? "") !== (originalScene[field] ?? "");
}

const HIGHLIGHT_FIELDS: readonly PersonaScriptHighlightField[] = [
  "seeing",
  "hearing",
  "product",
  "props",
];

/** `sceneNumber:field` keys of scene cells whose text differs from the original version. */
export function changedSceneCellKeys(
  scenes: PersonaScriptDto["scenes"],
  original: PersonaScriptDto | undefined,
): ReadonlySet<string> {
  const keys = new Set<string>();
  if (!original) return keys;
  for (const scene of scenes) {
    const originalScene = original.scenes.find((entry) => entry.sceneNumber === scene.sceneNumber);
    for (const field of HIGHLIGHT_FIELDS) {
      if (!originalScene || (scene[field] ?? "") !== (originalScene[field] ?? "")) {
        keys.add(sceneFieldBlameKey(scene.sceneNumber, field));
      }
    }
  }
  return keys;
}

export function personaScriptIdentityDiffersFromOriginal(
  preview: PersonaScriptDto | undefined,
  original: PersonaScriptDto | undefined,
  field: "hook" | "concept" | "presenter",
): boolean {
  if (!preview || !original) return false;
  return (preview[field] ?? "") !== (original[field] ?? "");
}

export function highlightedSceneClass(isDifferent: boolean): string {
  return isDifferent
    ? "border-l-2 border-l-ai bg-ai-subtle"
    : "border-l-2 border-l-transparent bg-background";
}

const SCENE_CHANGE_LABEL = /Scene\s+(\d+)\s*·\s*(Seeing|Hearing|Product|Props)\s*$/i;

const SCENE_FIELD_FROM_LABEL: Record<string, PersonaScriptHighlightField> = {
  seeing: "seeing",
  hearing: "hearing",
  product: "product",
  props: "props",
};

export function sceneFieldBlameKey(
  sceneNumber: number,
  field: PersonaScriptHighlightField,
): string {
  return `${sceneNumber}:${field}`;
}

export function parseSceneFieldChangeLabel(
  changeLabel: string | null | undefined,
): { sceneNumber: number; field: PersonaScriptHighlightField } | null {
  if (!changeLabel) return null;
  const match = changeLabel.trim().match(SCENE_CHANGE_LABEL);
  if (!match) return null;
  const field = SCENE_FIELD_FROM_LABEL[match[2]!.toLowerCase()];
  if (!field) return null;
  return { sceneNumber: Number(match[1]), field };
}

/** Last version ≤ `upToVersion` whose change label names this scene cell (merged labels list several). */
export function sceneFieldBlameByKey(
  versions: readonly { version: number; changeLabel: string | null }[],
  upToVersion: number,
): Map<string, number> {
  const blame = new Map<string, number>();
  const chronological = [...versions]
    .filter((entry) => entry.version > 1 && entry.version <= upToVersion)
    .sort((a, b) => a.version - b.version);

  for (const entry of chronological) {
    for (const part of splitVersionChangeLabel(entry.changeLabel)) {
      const parsed = parseSceneFieldChangeLabel(part);
      if (!parsed) continue;
      blame.set(sceneFieldBlameKey(parsed.sceneNumber, parsed.field), entry.version);
    }
  }

  return blame;
}
